import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../components/shell';
import { usePersistedState } from '../../hooks/usePersistedState';
import { C, FONT, SP } from '../../theme/tokens';

const KEY = 'cleanroom.calc.scanRate.v2';

type Fields = {
  upstreamConc: string;   // Cu — particles per ft³
  probeDim: string;       // Dp — probe dimension along scan direction (inches)
  leakCounts: string;     // Nr — counts that constitute a leak
  leakPenetration: string;// Pd — designated leak penetration fraction
  sampleFlow: string;     // Qs — counter sample flow rate (cfm)
};

const EMPTY: Fields = {
  upstreamConc: '',
  probeDim: '1.0',
  leakCounts: '1',
  leakPenetration: '0.0001',  // 0.01 % — typical HEPA leak criterion (IEST-RP-CC034)
  sampleFlow: '1',            // 1 cfm — standard discrete particle counter
};

const PENETRATION_PRESETS = [
  { value: '0.0001', label: '0.01 %' },   // HEPA
  { value: '0.00005', label: '0.005 %' }, // ULPA
  { value: '0.00001', label: '0.001 %' }, // stricter ULPA
];

export default function CleanroomScanRateScreen() {
  const [fields, setFields, { clear }] = usePersistedState<Fields>(KEY, EMPTY);
  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const result = useMemo(() => {
    const Cu = parseFloat(fields.upstreamConc);
    const Dp = parseFloat(fields.probeDim);
    const Nr = parseFloat(fields.leakCounts);
    const Pd = parseFloat(fields.leakPenetration);
    const Qs = parseFloat(fields.sampleFlow);

    if (![Cu, Dp, Nr, Pd, Qs].every((v) => Number.isFinite(v) && v > 0)) return null;

    // IEST-RP-CC034 scan rate (Imperial form):
    //   Sr [in/min] = (Pd · Cu · Qs · Dp) / Nr
    //   where Cu [particles/ft³] × Qs [ft³/min] gives particles/min through the probe,
    //   Pd scales by the acceptable leak penetration fraction,
    //   Dp / Sr gives the probe dwell time (min) over a point defect,
    //   and Nr is the integer count threshold.
    const srInPerMin = (Pd * Cu * Qs * Dp) / Nr;
    const srInPerSec = srInPerMin / 60;
    const srFpm = srInPerMin / 12;

    // Particles expected per pass at Sr:  N = Pd · Cu · Qs · Dp / Sr  == Nr (by design)
    // Expose the upstream count rate reaching the probe (if no leak) for sanity.
    const challengeRate = Cu * Qs; // particles/min passing the counter if all upstream air entered

    return { srInPerMin, srInPerSec, srFpm, challengeRate };
  }, [fields]);

  return (
    <CalcShell
      title="HEPA Scan Rate"
      subtitle="IEST-RP-CC034 · HEPA/ULPA leak-test probe speed"
      formula={
        <>
          <FormulaLine>Sr = (Pd × Cu × Qs × Dp) / Nr</FormulaLine>
          <FormulaNote>
            Sr = maximum probe scan rate (in/min){'\n'}
            Pd = designated leak penetration (fraction — e.g. 0.0001 for 0.01%){'\n'}
            Cu = upstream (challenge) aerosol concentration (particles/ft³){'\n'}
            Qs = counter sample flow rate (ft³/min — typ. 1 cfm){'\n'}
            Dp = probe dimension in direction of scan (in){'\n'}
            Nr = count threshold to declare a leak (typ. 1){'\n'}
            Per IEST-RP-CC034 and ISO 14644-3 Annex B.
          </FormulaNote>
        </>
      }
      onClear={() => { void clear(); }}
      outputSlots={{
        scan_rate_fpm: result ? result.srFpm : undefined,
      }}
    >
      <Text style={styles.sectionLabel}>Upstream Challenge</Text>
      <NumericField
        label="Upstream Concentration (Cu)"
        value={fields.upstreamConc}
        onChangeText={(v) => set({ upstreamConc: v })}
        rightText="particles/ft³"
        maxDecimals={0}
      />
      <NumericField
        label="Sample Flow Rate (Qs)"
        value={fields.sampleFlow}
        onChangeText={(v) => set({ sampleFlow: v })}
        rightText="cfm"
        maxDecimals={2}
      />

      <Text style={styles.sectionLabel}>Probe & Leak Criteria</Text>
      <NumericField
        label="Probe Width (Dp)"
        value={fields.probeDim}
        onChangeText={(v) => set({ probeDim: v })}
        rightText="in"
        maxDecimals={2}
      />
      <NumericField
        label="Counts that Constitute a Leak (Nr)"
        value={fields.leakCounts}
        onChangeText={(v) => set({ leakCounts: v })}
        rightText="counts"
        maxDecimals={0}
      />

      <Text style={styles.sectionLabel}>Designated Leak Penetration (Pd)</Text>
      <SegmentedButtons
        value={
          PENETRATION_PRESETS.some((p) => p.value === fields.leakPenetration)
            ? fields.leakPenetration
            : ''
        }
        onValueChange={(v) => v && set({ leakPenetration: v })}
        buttons={PENETRATION_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        style={styles.segmented}
      />
      <NumericField
        label="Custom Pd (decimal)"
        value={fields.leakPenetration}
        onChangeText={(v) => set({ leakPenetration: v })}
        rightText="fraction"
        maxDecimals={6}
      />

      {result && (
        <>
          <Text style={[styles.sectionLabel, { marginTop: SP.md }]}>Results</Text>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Maximum Scan Rate</Text>
            <Text style={styles.resultValue}>
              {result.srInPerSec.toFixed(2)} <Text style={styles.resultUnit}>in/sec</Text>
            </Text>
            <Text style={styles.resultSub}>
              {result.srInPerMin.toFixed(1)} in/min · {result.srFpm.toFixed(2)} ft/min
            </Text>
          </View>

          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Challenge Rate at Counter</Text>
            <Text style={styles.resultValue}>
              {result.challengeRate.toLocaleString('en-US', { maximumFractionDigits: 0 })}{' '}
              <Text style={styles.resultUnit}>particles/min</Text>
            </Text>
            <Text style={styles.resultSub}>Cu × Qs (if all upstream air passed the probe)</Text>
          </View>

          <Text style={styles.note}>
            Scan no faster than this rate. Reduce the rate if the probe dwell must catch ≥ Nr counts
            from the smallest leak you intend to resolve. Verify upstream concentration before
            and after each scan pass.
          </Text>
        </>
      )}
    </CalcShell>
  );
}

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: SP.md,
    marginBottom: SP.sm,
  },
  segmented: {
    marginBottom: 8,
  },
  resultCard: {
    backgroundColor: C.panel,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.edge,
    borderLeftWidth: 3,
    borderLeftColor: C.accent,
    paddingHorizontal: SP.md,
    paddingVertical: 10,
    marginBottom: SP.sm,
  },
  resultLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  resultValue: {
    fontFamily: FONT.mono,
    fontSize: 20,
    color: C.ink,
    fontWeight: '600',
  },
  resultUnit: {
    fontFamily: FONT.mono,
    fontSize: 13,
    color: C.dim,
    fontWeight: '400',
  },
  resultSub: {
    fontFamily: FONT.sans,
    fontSize: 11,
    color: C.dim,
    marginTop: 2,
  },
  note: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    lineHeight: 17,
    marginTop: SP.sm,
  },
});
